import { useCallback } from 'react';

import { useMutation } from '@apollo/client/react';
import { captureException } from '@sentry/react';

import { gql } from '@graphql/__generated__';
import { resizeImageToBase64 } from '@modules/_shared/components/image-gallery/enhance/enhance-image-utils';
import { processImage } from '@modules/_shared/components/image-upload/processors/imageProcessor';
import type { ProfilePhotoIssue } from '@modules/_shared/components/image-upload/profilePhotoIssue';
import type {
  ImageSource,
  ImageTransform,
} from '@modules/_shared/components/image-upload/types';
import { useLocale } from '@shared/hooks/locale';

const CHECK_PROFILE_PHOTO = gql(/* GraphQL */ `
  mutation CheckProfilePhoto($image_data: String!) {
    check_profile_photo(image_data: $image_data) {
      verdict
    }
  }
`);

const MIN_PHOTO_SIDE = 400;
const CHECK_MAX_DIMENSION = 512;

const isModelIssue = (verdict: string): verdict is ProfilePhotoIssue =>
  verdict === 'not_a_person' ||
  verdict === 'multiple_people' ||
  verdict === 'poor_quality';

const shortestSide = async (file: File) => {
  const bitmap = await createImageBitmap(file);
  const side = Math.min(bitmap.width, bitmap.height);
  bitmap.close();
  return side;
};

const askModel = async (
  file: File,
  check: (imageData: string) => Promise<string | null>,
): Promise<ProfilePhotoIssue | null> => {
  const objectUrl = URL.createObjectURL(file);
  try {
    const imageData = await resizeImageToBase64(objectUrl, CHECK_MAX_DIMENSION);
    const verdict = await check(imageData);
    return verdict && isModelIssue(verdict) ? verdict : null;
  } catch (error) {
    captureException(error, { tags: { feature: 'profile-photo-check' } });
    return null;
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
};

export const loadPhotoForCheck = (
  source: ImageSource,
  transform: ImageTransform,
  t: Parameters<typeof processImage>[2],
): Promise<File | null> =>
  processImage(source, transform, t).catch((error: unknown) => {
    captureException(error, { tags: { feature: 'profile-photo-check' } });
    return null;
  });

export const checkPhotoFile = async (
  file: File,
  check: (imageData: string) => Promise<string | null>,
): Promise<ProfilePhotoIssue | null> => {
  const [modelIssue, side] = await Promise.all([
    askModel(file, check),
    shortestSide(file).catch(() => null),
  ]);
  if (modelIssue) {
    return modelIssue;
  }
  return side != null && side < MIN_PHOTO_SIDE ? 'poor_quality' : null;
};

export const useProfilePhotoCheck = () => {
  const [checkProfilePhoto] = useMutation(CHECK_PROFILE_PHOTO);

  return useCallback(
    async (file: File | null) =>
      file
        ? checkPhotoFile(file, async image_data => {
            const { data } = await checkProfilePhoto({
              variables: { image_data },
            });
            return data?.check_profile_photo?.verdict ?? null;
          })
        : null,
    [checkProfilePhoto],
  );
};

export const useProfilePhotoIssueMessage = () => {
  const { t } = useLocale();
  return useCallback(
    (issue: ProfilePhotoIssue) => {
      switch (issue) {
        case 'not_a_person':
          return t(
            'This does not look like a profile photo. Logos, graphics and pictures without a face are not allowed. Please upload a clear headshot.',
          );
        case 'multiple_people':
          return t(
            'We see more than one person in this photo. Clients should see only you.',
          );
        case 'poor_quality':
          return t(
            'This photo looks blurry or too small. Clients will see it on your profile, so try a sharper, larger photo.',
          );
      }
    },
    [t],
  );
};
